import React, { useState } from 'react';
import { Box, Card, Grid, Typography, TextField, MenuItem, FormControlLabel, Switch, Collapse, IconButton, Button } from '@mui/material';
import { Controller, useWatch, useFormContext } from 'react-hook-form';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { LocalizationProvider, DesktopDateTimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import ImgUploadDialog from '../../../../components/ImgUpload/imgUpload';

const sectionTypes = [
    { value: 'horizontal_scroll', label: 'Horizontal Scroll' },
    { value: 'carousel', label: 'Carousel' },
    { value: 'grid_2_column', label: 'Grid 2 Column' },
    { value: 'grid_3_column', label: 'Grid 3 Column' },
    { value: 'grid_4_column', label: 'Grid 4 Column' },
    { value: 'fixed_banner', label: 'Fixed Banner' },
    { value: 'vertical_list', label: 'Vertical List' },
    { value: 'mixed_grid', label: 'Mixed Grid' }
];

const backgroundTypes = [
    { value: 'none', label: 'None' },
    { value: 'color', label: 'Color' },
    { value: 'gradient', label: 'Gradient' },
    { value: 'image', label: 'Image' },
    { value: 'gif', label: 'Gif' },
    { value: 'video', label: 'Video' }
];

const fontFamilyOptions = [
    { value: '', label: 'Default' },
    { value: 'Roboto', label: 'Roboto' },
    { value: 'Inter', label: 'Inter' },
    { value: 'Poppins', label: 'Poppins' },
    { value: 'Lato', label: 'Lato' },
    { value: 'Open Sans', label: 'Open Sans' },
    { value: 'Montserrat', label: 'Montserrat' },
    { value: 'Nunito', label: 'Nunito' },
    { value: 'Raleway', label: 'Raleway' },
];

const fontWeightOptions = [
    { value: '', label: 'Default' },
    { value: 300, label: 'Light (300)' },
    { value: 400, label: 'Regular (400)' },
    { value: 500, label: 'Medium (500)' },
    { value: 600, label: 'Semi Bold (600)' },
    { value: 700, label: 'Bold (700)' },
    { value: 800, label: 'Extra Bold (800)' },
];

const aspectRatioOptions = [
    { value: 'auto', label: 'Auto' },
    { value: '1:1', label: '1:1 (Circle)' },
    { value: '4:3', label: '4:3' },
    { value: '3:4', label: '3:4' },
    { value: '16:9', label: '16:9' },
    { value: '9:16', label: '9:16' },
    { value: '3:2', label: '3:2' },
];

const textAlignOptions = [
    { value: 'left', label: 'Left' },
    { value: 'center', label: 'Center' },
    { value: 'right', label: 'Right' },
];

const positionOptions = [
    { value: 'left', label: 'Left' },
    { value: 'center', label: 'Center' },
    { value: 'right', label: 'Right' }
];

const pageTypeOptions = [
    { value: 'section', label: 'Section' },
    { value: 'header', label: 'Header' },
    { value: 'footer', label: 'Footer' }
];

const SectionForm = ({ pageType = 'section' }) => {
    const { control, setValue, formState: { errors } } = useFormContext();
    const [displayOptionsExpanded, setDisplayOptionsExpanded] = useState(false);
    const [openImageUpload, setOpenImageUpload] = useState(false);
    const [currentFileField, setCurrentFileField] = useState('backgroundImage');

    const sectionType = useWatch({ control, name: 'sectionType' });
    const backgroundType = useWatch({ control, name: 'backgroundType' });
    const showViewAll = useWatch({ control, name: 'showViewAll' });
    const endDateTime = useWatch({ control, name: 'endDateTime' });
    const showTitle = useWatch({ control, name: 'displayOptions.showTitle' });
    const showSubTitle = useWatch({ control, name: 'displayOptions.showSubTitle' });
    const showDescription = useWatch({ control, name: 'displayOptions.showDescription' });
    const enableSubTitleBackgroundColor = useWatch({ control, name: 'displayOptions.enableSubTitleBackgroundColor' });
    const customFontName = useWatch({ control, name: 'displayOptions.customFontName' });
    const customFontUrl = useWatch({ control, name: 'displayOptions.customFontUrl' });
    const entityLabel = pageType === 'header' ? 'Header' : pageType === 'footer' ? 'Footer' : 'Section';
    const isFixedPageType = pageType === 'header' || pageType === 'footer';
    const isFooterPageType = pageType === 'footer';
    const isFontUpload = currentFileField === 'displayOptions.customFontUrl';
    const availableFontFamilyOptions = customFontUrl && customFontName
        ? [...fontFamilyOptions, { value: customFontName, label: `${customFontName} (Uploaded)` }]
        : fontFamilyOptions;

    const handleFileSelect = (fieldName) => {
        setCurrentFileField(fieldName);
        setOpenImageUpload(true);
    };

    const handleStartDateChange = (newValue, onChange) => {
        const nextStartDate = newValue ? newValue.format('YYYY-MM-DDTHH:mm') : null;
        onChange(nextStartDate);

        if (!nextStartDate) return;

        const shouldSyncEndDate = !endDateTime || dayjs(endDateTime).isBefore(dayjs(nextStartDate));
        if (shouldSyncEndDate) {
            setValue('endDateTime', nextStartDate, { shouldDirty: true, shouldValidate: true });
        }
    };

    const imgUploadResponse = (response) => {
        let imageUrl = response.imageBaseUrl + response.imageDirectory + response.imageName;
        setValue(currentFileField, imageUrl, { shouldDirty: true, shouldValidate: true });
        if (isFontUpload && !customFontName) {
            const uploadedFontName = response.imageName
                .replace(/\.[^.]+$/, '')
                .replace(/-\d{10,}$/, '')
                .replace(/[-_]+/g, ' ')
                .trim();
            setValue('displayOptions.customFontName', uploadedFontName || 'Custom Font', {
                shouldDirty: true,
                shouldValidate: true
            });
        }
        setOpenImageUpload(false);
        setCurrentFileField('backgroundImage');
    };

    const renderVisibilityControl = () => (
        <Controller
            name="displayOptions.isNotVisible"
            control={control}
            render={({ field }) => (
                <Box sx={{ border: '1px solid #eee', borderRadius: 1, px: 1.5, py: 1 }}>
                    <FormControlLabel
                        sx={{ m: 0, alignItems: 'flex-start' }}
                        control={
                            <Switch
                                checked={field.value === true}
                                onChange={(e) => field.onChange(e.target.checked)}
                                size="small"
                            />
                        }
                        label={
                            <Box>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Hide from Customer App</Typography>
                                <Typography sx={{ fontSize: '0.68rem', color: '#777' }}>
                                    Hidden entries are not returned by the public home-screen API.
                                </Typography>
                            </Box>
                        }
                    />
                </Box>
            )}
        />
    );

    if (isFooterPageType) {
        return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Grid item xs={12} md={8}>
                    <Card sx={{ p: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                                    Footer Title <Box component="span" sx={{ color: '#d32f2f' }}>*</Box>
                                </Typography>
                                <Controller
                                    name="title"
                                    control={control}
                                    rules={{ required: 'Footer title is required', maxLength: { value: 70, message: 'Max 70 characters' } }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            size="small"
                                            error={!!errors.title}
                                            helperText={errors.title?.message || `${field.value?.length || 0}/70`}
                                            InputProps={{ style: { fontSize: '0.75rem' } }}
                                            inputProps={{ maxLength: 70 }}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Subtitle</Typography>
                                <Controller
                                    name="subtitle"
                                    control={control}
                                    rules={{ maxLength: { value: 105, message: 'Max 105 characters' } }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            size="small"
                                            error={!!errors.subtitle}
                                            helperText={errors.subtitle?.message || `${field.value?.length || 0}/105`}
                                            InputProps={{ style: { fontSize: '0.75rem' } }}
                                            inputProps={{ maxLength: 105 }}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Description</Typography>
                                <Controller
                                    name="description"
                                    control={control}
                                    rules={{ maxLength: { value: 105, message: 'Max 105 characters' } }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            size="small"
                                            multiline
                                            rows={2}
                                            error={!!errors.description}
                                            helperText={errors.description?.message || `${field.value?.length || 0}/105`}
                                            InputProps={{ style: { fontSize: '0.75rem' } }}
                                            inputProps={{ maxLength: 105 }}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Mobile Image</Typography>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <Controller
                                        name="backgroundImage"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                disabled
                                                size="small"
                                                InputProps={{ style: { fontSize: '0.75rem' }, readOnly: true }}
                                                placeholder="No file selected"
                                                error={!!errors.backgroundImage}
                                                helperText={errors.backgroundImage?.message}
                                            />
                                        )}
                                    />
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        sx={{ whiteSpace: 'nowrap', fontSize: '0.7rem' }}
                                        onClick={() => handleFileSelect('backgroundImage')}
                                    >
                                        Upload
                                    </Button>
                                </Box>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Desktop Image</Typography>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <Controller
                                        name="desktopBackgroundImage"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                disabled
                                                size="small"
                                                InputProps={{ style: { fontSize: '0.75rem' }, readOnly: true }}
                                                placeholder="No file selected"
                                                error={!!errors.desktopBackgroundImage}
                                                helperText={errors.desktopBackgroundImage?.message}
                                            />
                                        )}
                                    />
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        sx={{ whiteSpace: 'nowrap', fontSize: '0.7rem' }}
                                        onClick={() => handleFileSelect('desktopBackgroundImage')}
                                    >
                                        Upload
                                    </Button>
                                </Box>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Start Date/Time</Typography>
                                <Controller
                                    name="startDateTime"
                                    control={control}
                                    render={({ field }) => (
                                        <DesktopDateTimePicker
                                            {...field}
                                            format="DD/MM/YYYY hh:mm A"
                                            value={field.value ? dayjs(field.value) : null}
                                            onChange={(newValue) => handleStartDateChange(newValue, field.onChange)}
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    size: 'small',
                                                    inputProps: { style: { fontSize: '0.75rem' } }
                                                }
                                            }}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>End Date/Time</Typography>
                                <Controller
                                    name="endDateTime"
                                    control={control}
                                    render={({ field }) => (
                                        <DesktopDateTimePicker
                                            {...field}
                                            format="DD/MM/YYYY hh:mm A"
                                            value={field.value ? dayjs(field.value) : null}
                                            onChange={(newValue) => field.onChange(newValue ? newValue.format('YYYY-MM-DDTHH:mm') : null)}
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    size: 'small',
                                                    inputProps: { style: { fontSize: '0.75rem' } }
                                                }
                                            }}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                {renderVisibilityControl()}
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Action Type</Typography>
                                <TextField
                                    value="URL"
                                    fullWidth
                                    disabled
                                    size="small"
                                    InputProps={{ style: { fontSize: '0.75rem' } }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Action URL</Typography>
                                <Controller
                                    name="displayOptions.actionData.url"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            size="small"
                                            placeholder="https://example.com"
                                            InputProps={{ style: { fontSize: '0.75rem' } }}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </Card>
                </Grid>

                <ImgUploadDialog
                    open={openImageUpload}
                    onClose={() => {
                        setOpenImageUpload(false);
                        setCurrentFileField('backgroundImage');
                    }}
                    imgData={{ path: 'customer-app/sections' }}
                    imgUploadResponse={imgUploadResponse}
                    allowedTypes={['image/png', 'image/jpeg', 'image/jpg', 'image/webp']}
                    allowedTypesErrorMsg="File type not supported. Please upload a PNG, JPEG, JPG, or WebP image."
                    maxFileSize={2 * 1024 * 1024}
                />
            </LocalizationProvider>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid item xs={12} md={8}>
                <Card sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                                {entityLabel} Title <Box component="span" sx={{ color: '#d32f2f' }}>*</Box>
                            </Typography>
                            <Controller
                                name="title"
                                control={control}
                                rules={{ required: 'Title is required', maxLength: { value: 70, message: 'Max 70 characters' } }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        size="small"
                                        error={!!errors.title}
                                        helperText={errors.title?.message || `${field.value?.length || 0}/70`}
                                        InputProps={{ style: { fontSize: '0.75rem' } }}
                                        inputProps={{ maxLength: 70 }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Subtitle <Box component="span" sx={{ color: '#d32f2f' }}>*</Box></Typography>
                            <Controller
                                name="subtitle"
                                control={control}
                                rules={{ required: 'Subtitle is required', maxLength: { value: 105, message: 'Max 105 characters' } }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        size="small"
                                        error={!!errors.subtitle}
                                        helperText={errors.subtitle?.message || `${field.value?.length || 0}/105`}
                                        InputProps={{ style: { fontSize: '0.75rem' } }}
                                        inputProps={{ maxLength: 105 }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Description <Box component="span" sx={{ color: '#d32f2f' }}>*</Box></Typography>
                            <Controller
                                name="description"
                                control={control}
                                rules={{ required: 'Description is required', maxLength: { value: 105, message: 'Max 105 characters' } }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        size="small"
                                        multiline
                                        rows={2}
                                        error={!!errors.description}
                                        helperText={errors.description?.message || `${field.value?.length || 0}/105`}
                                        InputProps={{ style: { fontSize: '0.75rem' } }}
                                        inputProps={{ maxLength: 105 }}
                                    />
                                )}
                            />
                        </Grid>

                        {!isFixedPageType && <Grid item xs={12} sm={6}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Page Type</Typography>
                            <Controller
                                name="displayOptions.pageType"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        select
                                        fullWidth
                                        size="small"
                                        InputProps={{ style: { fontSize: '0.75rem' } }}
                                    >
                                        {pageTypeOptions.map(opt => (
                                            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.75rem' }}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            />
                        </Grid>}

                        {!isFixedPageType && <Grid item xs={12} sm={6}>
                            <Controller
                                name="displayOptions.requiresServiceMapping"
                                control={control}
                                render={({ field }) => (
                                    <Box sx={{ border: '1px solid #eee', borderRadius: 1, px: 1.5, py: 1 }}>
                                        <FormControlLabel
                                            sx={{ m: 0, alignItems: 'flex-start' }}
                                            control={
                                                <Switch
                                                    checked={!(field.value ?? true)}
                                                    onChange={(e) => field.onChange(!e.target.checked)}
                                                    size="small"
                                                />
                                            }
                                            label={
                                                <Box>
                                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Show Section Without Service</Typography>
                                                    <Typography sx={{ fontSize: '0.68rem', color: '#777' }}>
                                                        Enable this when the section should appear without mapped child service data.
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </Box>
                                )}
                            />
                        </Grid>}

                        <Grid item xs={12} sm={6}>
                            {renderVisibilityControl()}
                        </Grid>

                        <Grid item xs={12}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    cursor: 'pointer',
                                    py: 1,
                                    borderBottom: '1px solid #eee',
                                    '&:hover': { backgroundColor: '#fafafa' }
                                }}
                                onClick={() => setDisplayOptionsExpanded(!displayOptionsExpanded)}
                            >
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Display Options</Typography>
                                <IconButton size="small">
                                    {displayOptionsExpanded ? <ExpandLess /> : <ExpandMore />}
                                </IconButton>
                            </Box>
                            <Collapse in={displayOptionsExpanded}>
                                <Grid container spacing={1} sx={{ mt: 1 }}>
                                    <Grid item xs={12} sm={4}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Controller
                                                name="displayOptions.showTitle"
                                                control={control}
                                                render={({ field }) => (
                                                    <FormControlLabel
                                                        control={<Switch {...field} checked={field.value} size="small" />}
                                                        label={<Typography sx={{ fontSize: '0.75rem' }}>Show Title</Typography>}
                                                    />
                                                )}
                                            />
                                            {showTitle && (
                                                <Controller
                                                    name="displayOptions.titlePosition"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <TextField
                                                            {...field}
                                                            select
                                                            size="small"
                                                            sx={{ width: 80 }}
                                                            InputProps={{ style: { fontSize: '0.7rem' } }}
                                                        >
                                                            {positionOptions.map(opt => (
                                                                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.7rem' }}>
                                                                    {opt.label}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>
                                                    )}
                                                />
                                            )}
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Controller
                                                name="displayOptions.showSubTitle"
                                                control={control}
                                                render={({ field }) => (
                                                    <FormControlLabel
                                                        control={<Switch {...field} checked={field.value} size="small" />}
                                                        label={<Typography sx={{ fontSize: '0.75rem' }}>Show Subtitle</Typography>}
                                                    />
                                                )}
                                            />
                                            {showSubTitle && (
                                                <Controller
                                                    name="displayOptions.subTitlePosition"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <TextField
                                                            {...field}
                                                            select
                                                            size="small"
                                                            sx={{ width: 80 }}
                                                            InputProps={{ style: { fontSize: '0.7rem' } }}
                                                        >
                                                            {positionOptions.map(opt => (
                                                                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.7rem' }}>
                                                                    {opt.label}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>
                                                    )}
                                                />
                                            )}
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Controller
                                                name="displayOptions.showDescription"
                                                control={control}
                                                render={({ field }) => (
                                                    <FormControlLabel
                                                        control={<Switch {...field} checked={field.value} size="small" />}
                                                        label={<Typography sx={{ fontSize: '0.75rem' }}>Show Description</Typography>}
                                                    />
                                                )}
                                            />
                                            {showDescription && (
                                                <Controller
                                                    name="displayOptions.descriptionPosition"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <TextField
                                                            {...field}
                                                            select
                                                            size="small"
                                                            sx={{ width: 80 }}
                                                            InputProps={{ style: { fontSize: '0.7rem' } }}
                                                        >
                                                            {positionOptions.map(opt => (
                                                                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.7rem' }}>
                                                                    {opt.label}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>
                                                    )}
                                                />
                                            )}
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography sx={{ fontSize: '0.7rem', minWidth: 64, flexShrink: 0 }}>Title Align</Typography>
                                            <Controller
                                                name="displayOptions.titleTextAlign"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        select
                                                        size="small"
                                                        sx={{ flex: 1 }}
                                                        InputProps={{ style: { fontSize: '0.7rem' } }}
                                                    >
                                                        {textAlignOptions.map(opt => (
                                                            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.7rem' }}>
                                                                {opt.label}
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>
                                                )}
                                            />
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography sx={{ fontSize: '0.7rem', minWidth: 64, flexShrink: 0 }}>Subtitle Align</Typography>
                                            <Controller
                                                name="displayOptions.subTitleTextAlign"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        select
                                                        size="small"
                                                        sx={{ flex: 1 }}
                                                        InputProps={{ style: { fontSize: '0.7rem' } }}
                                                    >
                                                        {textAlignOptions.map(opt => (
                                                            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.7rem' }}>
                                                                {opt.label}
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>
                                                )}
                                            />
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography sx={{ fontSize: '0.7rem', minWidth: 64, flexShrink: 0 }}>Desc Align</Typography>
                                            <Controller
                                                name="displayOptions.descriptionTextAlign"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        select
                                                        size="small"
                                                        sx={{ flex: 1 }}
                                                        InputProps={{ style: { fontSize: '0.7rem' } }}
                                                    >
                                                        {textAlignOptions.map(opt => (
                                                            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.7rem' }}>
                                                                {opt.label}
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>
                                                )}
                                            />
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Collapse>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Title Color</Typography>
                            <Controller
                                name="displayOptions.titleColor"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        type="color"
                                        fullWidth
                                        size="small"
                                        InputProps={{ style: { fontSize: '0.75rem' } }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Subtitle Color</Typography>
                            <Controller
                                name="displayOptions.subTitleColor"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        type="color"
                                        fullWidth
                                        size="small"
                                        InputProps={{ style: { fontSize: '0.75rem' } }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Description Color</Typography>
                            <Controller
                                name="displayOptions.descriptionColor"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        type="color"
                                        fullWidth
                                        size="small"
                                        InputProps={{ style: { fontSize: '0.75rem' } }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Subtitle Highlight Color</Typography>
                            <Controller
                                name="displayOptions.subTitleBackgroundColor"
                                control={control}
                                render={({ field }) => (
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <TextField
                                            {...field}
                                            value={field.value === 'none' ? '#FFFFFF' : field.value || '#FFFFFF'}
                                            onChange={(e) => field.onChange(e.target.value)}
                                            type="color"
                                            fullWidth
                                            size="small"
                                            disabled={!enableSubTitleBackgroundColor}
                                            InputProps={{ style: { fontSize: '0.75rem' } }}
                                        />
                                        <Controller
                                            name="displayOptions.enableSubTitleBackgroundColor"
                                            control={control}
                                            render={({ field }) => (
                                                <Switch
                                                    checked={!!field.value}
                                                    onChange={(e) => field.onChange(e.target.checked)}
                                                    size="small"
                                                />
                                            )}
                                        />
                                    </Box>
                                )}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Custom Font</Typography>
                            <Grid container spacing={1}>
                                <Grid item xs={12} sm={4}>
                                    <Controller
                                        name="displayOptions.customFontName"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                size="small"
                                                placeholder="Font family name"
                                                InputProps={{ style: { fontSize: '0.75rem' } }}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={8}>
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: 'minmax(0, 1fr) 112px',
                                            gap: 1,
                                            alignItems: 'start'
                                        }}
                                    >
                                        <Controller
                                            name="displayOptions.customFontUrl"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    disabled
                                                    size="small"
                                                    placeholder="No TTF, OTF, WOFF, or WOFF2 font selected"
                                                    InputProps={{ style: { fontSize: '0.75rem' }, readOnly: true }}
                                                />
                                            )}
                                        />
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            sx={{
                                                whiteSpace: 'nowrap',
                                                fontSize: '0.7rem',
                                                width: 112,
                                                minWidth: 112,
                                                height: '40px !important',
                                                minHeight: '40px !important',
                                                maxHeight: '40px',
                                                p: 0,
                                                boxSizing: 'border-box',
                                                alignSelf: 'start'
                                            }}
                                            onClick={() => handleFileSelect('displayOptions.customFontUrl')}
                                        >
                                            Upload Font
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                            <Typography sx={{ mt: 0.5, fontSize: '0.68rem', color: '#777' }}>
                                After upload, select the uploaded font from any font-family field below.
                            </Typography>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Title Font Family</Typography>
                            <Controller
                                name="displayOptions.titleFontFamily"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        select
                                        fullWidth
                                        size="small"
                                        InputProps={{ style: { fontSize: '0.75rem', fontFamily: field.value } }}
                                    >
                                        {availableFontFamilyOptions.map(f => (
                                            <MenuItem key={f.value} value={f.value} sx={{ fontSize: '0.7rem', fontFamily: f.value }}>
                                                {f.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Subtitle Font Family</Typography>
                            <Controller
                                name="displayOptions.subTitleFontFamily"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        select
                                        fullWidth
                                        size="small"
                                        InputProps={{ style: { fontSize: '0.75rem', fontFamily: field.value } }}
                                    >
                                        {availableFontFamilyOptions.map(f => (
                                            <MenuItem key={f.value} value={f.value} sx={{ fontSize: '0.7rem', fontFamily: f.value }}>
                                                {f.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Description Font Family</Typography>
                            <Controller
                                name="displayOptions.descriptionFontFamily"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        select
                                        fullWidth
                                        size="small"
                                        InputProps={{ style: { fontSize: '0.75rem', fontFamily: field.value } }}
                                    >
                                        {availableFontFamilyOptions.map(f => (
                                            <MenuItem key={f.value} value={f.value} sx={{ fontSize: '0.7rem', fontFamily: f.value }}>
                                                {f.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            />
                        </Grid>

                        {[
                            { key: 'title', label: 'Title', defaultSize: 20 },
                            { key: 'subTitle', label: 'Subtitle', defaultSize: 14 },
                            { key: 'description', label: 'Description', defaultSize: 12 },
                        ].map(textStyle => (
                            <React.Fragment key={textStyle.key}>
                                <Grid item xs={12} sm={4}>
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                                        {textStyle.label} Font Size (px)
                                    </Typography>
                                    <Controller
                                        name={`displayOptions.${textStyle.key}FontSize`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                type="number"
                                                fullWidth
                                                size="small"
                                                placeholder={textStyle.defaultSize.toString()}
                                                InputProps={{ style: { fontSize: '0.75rem' } }}
                                                inputProps={{ min: 8, max: 72, step: 1 }}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                                        {textStyle.label} Font Weight
                                    </Typography>
                                    <Controller
                                        name={`displayOptions.${textStyle.key}FontWeight`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                select
                                                fullWidth
                                                size="small"
                                                InputProps={{ style: { fontSize: '0.75rem' } }}
                                            >
                                                {fontWeightOptions.map(option => (
                                                    <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.7rem' }}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                    />
                                </Grid>
                            </React.Fragment>
                        ))}

                        <Grid item xs={12} sm={4}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Heading–Subtext Space (px)</Typography>
                            <Controller
                                name="displayOptions.headingSubtextSpacing"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        type="number"
                                        fullWidth
                                        size="small"
                                        InputProps={{ style: { fontSize: '0.75rem' } }}
                                        inputProps={{ min: 0, max: 100, step: 1 }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>View All Font Size (px)</Typography>
                            <Controller
                                name="displayOptions.viewAllFontSize"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        type="number"
                                        fullWidth
                                        size="small"
                                        InputProps={{ style: { fontSize: '0.75rem' } }}
                                        inputProps={{ min: 8, max: 72, step: 1 }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>View All Font Weight</Typography>
                            <Controller
                                name="displayOptions.viewAllFontWeight"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        select
                                        fullWidth
                                        size="small"
                                        InputProps={{ style: { fontSize: '0.75rem' } }}
                                    >
                                        {fontWeightOptions.map(option => (
                                            <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.7rem' }}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Text Gap (px)</Typography>
                            <Controller
                                name="displayOptions.bookAgainTimeGap"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        type="number"
                                        fullWidth
                                        size="small"
                                        InputProps={{ style: { fontSize: '0.75rem' } }}
                                        inputProps={{ min: 0, max: 40, step: 1 }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                                {entityLabel} Layout Type <Box component="span" sx={{ color: '#d32f2f' }}>*</Box>
                            </Typography>
                            <Controller
                                name="sectionType"
                                control={control}
                                rules={{ required: 'Section type is required' }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        select
                                        fullWidth
                                        size="small"
                                        error={!!errors.sectionType}
                                        helperText={errors.sectionType?.message}
                                        InputProps={{ style: { fontSize: '0.75rem' } }}
                                    >
                                        {sectionTypes.map(type => (
                                            <MenuItem key={type.value} value={type.value} sx={{ fontSize: '0.75rem' }}>
                                                {type.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            />
                        </Grid>

                        {/* <Grid item xs={12} sm={6}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Position</Typography>
                            <Controller
                                name="position"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        type="number"
                                        disabled
                                        fullWidth
                                        size="small"
                                        InputProps={{ style: { fontSize: '0.75rem' } }}
                                        inputProps={{
                                            style: { textAlign: 'center', height: '1rem', fontSize: '0.75rem' },
                                            onFocus: (e) => e.target.select(), // This will auto-select all text on focus/click
                                            onClick: (e) => e.target.select(),  // This ensures selection even on click when already focused
                                            onWheel: (e) => e.target.blur(),  // Prevent scroll wheel
                                            onKeyDown: (e) => {
                                                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                                    e.preventDefault();
                                                }
                                            }
                                        }}
                                        onChange={(e) => {
                                            const inputValue = e.target.value;
                                            let finalValue;
                                            if (inputValue === '' || /^\d*\.?\d*$/.test(inputValue)) {
                                            
                                                finalValue = formatValue(inputValue);
                                                console.log("finalValue",finalValue)

                                                if(finalValue==''){
                                                    // finalValue=0
                                                }
                                                else {
                                                    const numValue = parseFloat(finalValue);
                                                    if (!isNaN(numValue) && numValue > 1000) {
                                                        finalValue = '1000';
                                                    }
                                                }

                                                field.onChange(finalValue);
                                            }
                                        }} 
                                    />
                                )}
                            />
                        </Grid> */}

                        <Grid item xs={12} sm={6}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Background Type</Typography>
                            <Controller
                                name="backgroundType"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        select
                                        fullWidth
                                        size="small"
                                        InputProps={{ style: { fontSize: '0.75rem' } }}
                                    >
                                        {backgroundTypes.map(type => (
                                            <MenuItem key={type.value} value={type.value} sx={{ fontSize: '0.75rem' }}>
                                                {type.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            />
                        </Grid>

                        {backgroundType === 'color' && (
                            <Grid item xs={12} sm={6}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Background Color</Typography>
                                <Controller
                                    name="backgroundColor"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            type="color"
                                            fullWidth
                                            size="small"
                                            InputProps={{ style: { fontSize: '0.75rem' } }}
                                        />
                                    )}
                                />
                            </Grid>
                        )}

                        {backgroundType === 'gradient' && (
                            <>
                                <Grid item xs={12} sm={3}>
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Start Color</Typography>
                                    <Controller
                                        name="displayOptions.gradientStartColor"
                                        control={control}
                                        defaultValue="#667eea"
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                type="color"
                                                fullWidth
                                                size="small"
                                                InputProps={{ style: { fontSize: '0.75rem' } }}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>End Color</Typography>
                                    <Controller
                                        name="displayOptions.gradientEndColor"
                                        control={control}
                                        defaultValue="#764ba2"
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                type="color"
                                                fullWidth
                                                size="small"
                                                InputProps={{ style: { fontSize: '0.75rem' } }}
                                            />
                                        )}
                                    />
                                </Grid>
                            </>
                        )}

                        {(backgroundType === 'image' || backgroundType === 'gif' || backgroundType === 'video') && (
                            <>
                                <Grid item xs={12} sm={6}>
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Mobile Background File</Typography>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <Controller
                                            name="backgroundImage"
                                            rules={{
                                                required: (backgroundType === 'image' || backgroundType === 'gif' || backgroundType === 'video') ? 'Mobile background file is required' : false
                                            }}
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    disabled
                                                    size="small"
                                                    InputProps={{ style: { fontSize: '0.75rem' }, readOnly: true }}
                                                    placeholder="No file selected"
                                                    error={!!errors.backgroundImage}
                                                    helperText={errors.backgroundImage?.message}
                                                />
                                            )}
                                        />
                                        <Button
                                            variant="outlined"
                                            component="label"
                                            size="small"
                                            sx={{ whiteSpace: 'nowrap', fontSize: '0.7rem' }}
                                            onClick={() => handleFileSelect('backgroundImage')}
                                        >
                                            Upload
                                        </Button>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Desktop Background File</Typography>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <Controller
                                            name="desktopBackgroundImage"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    disabled
                                                    size="small"
                                                    InputProps={{ style: { fontSize: '0.75rem' }, readOnly: true }}
                                                    placeholder="No file selected"
                                                    error={!!errors.desktopBackgroundImage}
                                                    helperText={errors.desktopBackgroundImage?.message}
                                                />
                                            )}
                                        />
                                        <Button
                                            variant="outlined"
                                            component="label"
                                            size="small"
                                            sx={{ whiteSpace: 'nowrap', fontSize: '0.7rem' }}
                                            onClick={() => handleFileSelect('desktopBackgroundImage')}
                                        >
                                            Upload
                                        </Button>
                                    </Box>
                                </Grid>
                            </>
                        )}

                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="showViewAll"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={<Switch {...field} checked={field.value} size="small" />}
                                        label={<Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Show View All</Typography>}
                                    />
                                )}
                            />
                        </Grid>

                        {showViewAll && (
                            <>
                                <Grid item xs={12} sm={6}>
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>View All Text{showViewAll && <Box component="span" sx={{ color: 'red' }}>*</Box>}</Typography>
                                    <Controller
                                        name="viewAllText"
                                        control={control}
                                        rules={{ required: showViewAll ? 'View All text is required' : false }}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                size="small"
                                                InputProps={{ style: { fontSize: '0.75rem' } }}
                                                error={!!errors.viewAllText}
                                            // helperText={errors.viewAllText?.message}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>View All Route</Typography>
                                    <Controller
                                        name="viewAllRoute"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                size="small"
                                                InputProps={{ style: { fontSize: '0.75rem' } }}
                                            />
                                        )}
                                    />
                                </Grid>
                            </>
                        )}

                        <Grid item xs={12} sm={6}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Start Date/Time</Typography>
                            <Controller
                                name="startDateTime"
                                control={control}
                                render={({ field }) => (
                                    <DesktopDateTimePicker
                                        {...field}
                                        format="DD/MM/YYYY hh:mm A"
                                        value={field.value ? dayjs(field.value) : null}
                                        onChange={(newValue) => handleStartDateChange(newValue, field.onChange)}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                size: 'small',
                                                inputProps: { style: { fontSize: '0.75rem' } }
                                            }
                                        }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>End Date/Time</Typography>
                            <Controller
                                name="endDateTime"
                                control={control}
                                render={({ field }) => (
                                    <DesktopDateTimePicker
                                        {...field}
                                        format="DD/MM/YYYY hh:mm A"
                                        value={field.value ? dayjs(field.value) : null}
                                        onChange={(newValue) => field.onChange(newValue ? newValue.format('YYYY-MM-DDTHH:mm') : null)}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                size: 'small',
                                                inputProps: { style: { fontSize: '0.75rem' } }
                                            }
                                        }}
                                    />
                                )}
                            />
                        </Grid>

                        {sectionType === 'carousel' && (
                            <Grid item xs={12} sm={6}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Auto Scroll Duration (sec)</Typography>
                                <Controller
                                    name="autoScrollDuration"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            type="number"
                                            fullWidth
                                            size="small"
                                            InputProps={{ style: { fontSize: '0.75rem' } }}
                                        />
                                    )}
                                />
                            </Grid>
                        )}

                        <Grid item xs={12} sm={6}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Max Items To Show</Typography>
                            <Controller
                                name="maxItemsToShow"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        type="number"
                                        fullWidth
                                        size="small"
                                        InputProps={{ style: { fontSize: '0.75rem' } }}
                                        inputProps={{ min: 1, max: 100 }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Aspect Ratio (Item image)</Typography>
                            <Controller
                                name="displayOptions.aspectRatio"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        select
                                        fullWidth
                                        size="small"
                                        InputProps={{ style: { fontSize: '0.75rem' } }}
                                    >
                                        {aspectRatioOptions.map(opt => (
                                            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.75rem' }}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={3}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Item Width (px)</Typography>
                            <Controller
                                name="displayOptions.cardWidth"
                                control={control}
                                rules={{
                                    validate: value => value === '' || value == null || Number(value) > 0 || 'Width must be greater than 0'
                                }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        type="number"
                                        fullWidth
                                        size="small"
                                        placeholder="Auto"
                                        error={!!errors.displayOptions?.cardWidth}
                                        helperText={errors.displayOptions?.cardWidth?.message}
                                        InputProps={{ style: { fontSize: '0.75rem' } }}
                                        inputProps={{ min: 1, step: 1 }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={3}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Item Height (px)</Typography>
                            <Controller
                                name="displayOptions.cardHeight"
                                control={control}
                                rules={{
                                    validate: value => value === '' || value == null || Number(value) > 0 || 'Height must be greater than 0'
                                }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        type="number"
                                        fullWidth
                                        size="small"
                                        placeholder="Auto"
                                        error={!!errors.displayOptions?.cardHeight}
                                        helperText={errors.displayOptions?.cardHeight?.message}
                                        InputProps={{ style: { fontSize: '0.75rem' } }}
                                        inputProps={{ min: 1, step: 1 }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Section Padding (px)</Typography>
                            <Grid container spacing={1} sx={{ mt: 0.5 }}>
                                {['top', 'right', 'bottom', 'left'].map(side => (
                                    <Grid item xs={6} sm={3} key={side}>
                                        <Controller
                                            name={`displayOptions.sectionPadding.${side}`}
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    type="number"
                                                    size="small"
                                                    fullWidth
                                                    label={side.charAt(0).toUpperCase() + side.slice(1)}
                                                    InputProps={{ style: { fontSize: '0.75rem' } }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Section Margin (px)</Typography>
                            <Grid container spacing={1} sx={{ mt: 0.5 }}>
                                {['top', 'right', 'bottom', 'left'].map(side => (
                                    <Grid item xs={6} sm={3} key={side}>
                                        <Controller
                                            name={`displayOptions.sectionMargin.${side}`}
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    type="number"
                                                    size="small"
                                                    fullWidth
                                                    label={side.charAt(0).toUpperCase() + side.slice(1)}
                                                    InputProps={{ style: { fontSize: '0.75rem' } }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>

                        {/* <Grid item xs={12} sm={6}>
                            <Controller
                                name="isActive"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={<Switch {...field} checked={field.value} size="small" />}
                                        label={<Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Active</Typography>}
                                    />
                                )}
                            />
                        </Grid> */}
                    </Grid>
                </Card>
            </Grid>

            <ImgUploadDialog
                open={openImageUpload}
                onClose={() => {
                    setOpenImageUpload(false);
                    setCurrentFileField('backgroundImage');
                }}
                imgData={{ path: 'customer-app/sections' }}
                imgUploadResponse={imgUploadResponse}
                allowedTypes={
                    isFontUpload
                        ? [
                            'font/ttf', 'font/otf', 'font/woff', 'font/woff2',
                            'application/x-font-ttf', 'application/x-font-otf',
                            'application/font-sfnt', 'application/font-woff',
                            'application/octet-stream'
                        ]
                    : backgroundType === 'gif'
                        ? ['image/gif']
                        : backgroundType === 'video'
                        ? ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime']
                        : ['image/png', 'image/jpeg', 'image/jpg', 'image/webp']
                }
                allowedTypesErrorMsg={
                    isFontUpload
                        ? 'File type not supported. Please upload a TTF, OTF, WOFF, or WOFF2 font.'
                    : backgroundType === 'gif'
                        ? 'File type not supported. Please upload a GIF image.'
                        : backgroundType === 'video'
                        ? 'File type not supported. Please upload an MP4, WebM, or OGG video.'
                        : 'File type not supported. Please upload a PNG, JPEG, JPG, or WebP image.'
                }
                maxFileSize={
                    isFontUpload
                        ? 5 * 1024 * 1024
                    : backgroundType === 'gif'
                        ? 10 * 1024 * 1024
                        : backgroundType === 'video'
                        ? 20 * 1024 * 1024
                        : 2 * 1024 * 1024
                }
                keepOriginal={isFontUpload || backgroundType === 'gif'}
                allowedExtensions={isFontUpload ? ['ttf', 'otf', 'woff', 'woff2'] : null}
                dialogTitle={isFontUpload ? 'Font Upload' : 'Media Upload'}
                chooseLabel={isFontUpload ? 'Choose Font' : 'Choose File'}
                uploadLabel={isFontUpload ? 'Upload Font' : 'Upload File'}
            />
        </LocalizationProvider>
    );
};

export default SectionForm;
